import {
  FileTextOutlined,
  LoadingOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { Alert, Button, Space, Tag, Tooltip } from 'antd'
import { useRuntimeVariables } from 'app-kit'
import { AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import { DocumentDataContext } from 'features/libra/document/DocumentDataContext'
import { LibraDocumentContext } from 'features/libra/document/LibraDocumentContext'
import { useDocumentProcessingReady } from 'features/libra/document/useDocumentProcessingReady'
import { stampArrayRowIds } from 'features/libra/fieldValues/arrayRowIds'
import { withFieldOrigin } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  ApmCitation,
  DocumentCitation,
  FieldValue,
  FieldValues,
  FieldValuesSchema,
  LlmCitation,
} from 'features/libra/ontology/ontologyFieldTypes'
import { AiPrefillButton } from 'features/libra/stepComponents/AiPrefillButton'
import {
  AiPrefillGate,
  prefillLabel,
} from 'features/libra/stepComponents/AiPrefillControls'
import { usePrefillBlockedReason } from 'features/libra/stepComponents/prefillGate'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useReprocessDocuments } from 'features/rolodex'
import startCase from 'lodash/startCase'
import type { FC } from 'react'
import { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import cantileverTheme from 'styles/cantileverTheme'
import { RolodexPermissionsValues } from 'types'

/** Identifies a field the AI attempted but could not extract from documents. */
export function isAiUnextracted(fieldValue: FieldValue | undefined): boolean {
  return fieldValue?.aiExtracted === false
}

export const AiAutofillAlert = styled(Alert)`
  min-height: 48px;

  .ant-alert-icon {
    color: ${cantileverTheme.colors.OceanBlue} !important;
  }
`

/**
 * Safely reads the citation from a field value's llmValue envelope.
 *
 * gong-agent-service wraps llmValue as `{ value, citation }`. Older records
 * or test fixtures may have llmValue as a plain scalar, so this helper handles
 * both shapes gracefully.
 */
export function extractLlmCitation(
  fieldValue: FieldValue | undefined
): LlmCitation | null {
  if (!fieldValue?.llmValue) return null
  const lv = fieldValue.llmValue
  if (typeof lv === 'object' && lv !== null && 'citation' in (lv as object)) {
    return (
      (lv as { value: unknown; citation: LlmCitation | null }).citation ?? null
    )
  }
  return null
}

function isDocumentCitation(c: LlmCitation): c is DocumentCitation {
  // Some AI responses omit `doc`; treating those as filenames crashes shortDocName.
  return (
    c !== null &&
    typeof (c as DocumentCitation).doc === 'string' &&
    (c as DocumentCitation).doc !== 'APM component data'
  )
}

function isApmCitation(c: LlmCitation): c is ApmCitation {
  return c !== null && (c as ApmCitation).doc === 'APM component data'
}

function shortDocName(doc: string): string {
  const base = doc.split('/').pop() ?? doc
  return base.length > 30 ? base.slice(0, 27) + '...' : base
}

const CitationTag = styled(Tag)`
  cursor: default;
  font-size: 11px;
  margin: 0;
`

/**
 * Matches a citation's `doc` (filename from the LLM) against the list of
 * documents in the Libra viewer. Tries exact match first, then suffix match
 * for paths where the LLM only sees the base filename.
 */
function findEntryId(
  docName: string,
  documentsByCategory: ReadonlyMap<
    string,
    ReadonlyArray<{
      entry_id: string
      filename?: string
      visible_name?: string
    }>
  >
): string | null {
  const needle = docName.toLowerCase()
  for (const docs of documentsByCategory.values()) {
    for (const doc of docs) {
      const candidates = [doc.filename, doc.visible_name].filter(
        Boolean
      ) as string[]
      for (const candidate of candidates) {
        const c = candidate.toLowerCase()
        if (
          c === needle ||
          c.endsWith('/' + needle) ||
          c.endsWith('\\' + needle)
        ) {
          return doc.entry_id
        }
      }
    }
  }
  return null
}

export const AiCitationChip: FC<{ fieldValue: FieldValue | undefined }> = ({
  fieldValue,
}) => {
  // Direct context access lets citations render safely outside these providers.
  const libraDoc = useContext(LibraDocumentContext)
  const docData = useContext(DocumentDataContext)

  if (!fieldValue) return null

  const valueIsEmpty =
    fieldValue.value === null ||
    fieldValue.value === undefined ||
    (Array.isArray(fieldValue.value) && fieldValue.value.length === 0)
  if (valueIsEmpty) return null

  const citation = extractLlmCitation(fieldValue)

  if (citation !== null) {
    if (isDocumentCitation(citation)) {
      const label = [
        shortDocName(citation.doc),
        citation.page !== null ? `p.${citation.page}` : null,
      ]
        .filter(Boolean)
        .join(' - ')

      const tooltipContent = (
        <div style={{ maxWidth: 320 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {citation.doc}
            {citation.page !== null ? `, page ${citation.page}` : ''}
          </div>
          {citation.excerpt && (
            <div style={{ fontStyle: 'italic' }}>
              &quot;{citation.excerpt}&quot;
            </div>
          )}
        </div>
      )

      const entryId =
        libraDoc && docData
          ? findEntryId(citation.doc, docData.documentsByCategory)
          : null

      const handleClick = entryId
        ? () =>
            libraDoc!.setSelectedDocumentId(entryId, citation.page ?? undefined)
        : undefined

      return (
        <Tooltip title={tooltipContent}>
          <CitationTag
            icon={<FileTextOutlined />}
            color='blue'
            onClick={handleClick}
            style={{ cursor: handleClick ? 'pointer' : 'default' }}>
            {label}
          </CitationTag>
        </Tooltip>
      )
    }

    if (isApmCitation(citation)) {
      // Zone pre-fill responses may omit display context while retaining the source.
      const component =
        typeof citation.component === 'string'
          ? citation.component.trim()
          : null
      const field =
        typeof citation.field === 'string' ? citation.field.trim() : null
      const context = [
        component ? `APM component: ${component}` : null,
        field ? `Field: ${startCase(field)}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
      const tooltipContent = `${
        context || 'Sourced from APM component data'
      }${citation.excerpt ? ` — "${citation.excerpt}"` : ''}`
      return (
        <Tooltip title={tooltipContent}>
          <CitationTag color='purple'>
            {component ? `APM - ${component}` : 'APM source'}
          </CitationTag>
        </Tooltip>
      )
    }
  }

  if (fieldValue.aiExtracted === false) {
    const searched = fieldValue.documentsSearched ?? []
    const tooltipContent =
      searched.length > 0
        ? `Searched: ${searched.join(', ')}`
        : 'No relevant content found in available documents'
    return (
      <Tooltip title={tooltipContent}>
        <CitationTag color='warning'>Not in documents</CitationTag>
      </Tooltip>
    )
  }

  return null
}

/**
 * Assesses one agent section and applies the shared provenance and row-ID rules
 * used by both single-section and multi-section pre-fill actions. Every
 * returned wrapper, including a not-found one, is marked `llm`.
 */
export async function postAssessSection(
  axios: AuthedAxiosRequests,
  gongAgentUrl: string,
  {
    ticketId,
    sectionId,
    inputs,
  }: {
    ticketId: string
    sectionId: string
    inputs?: Record<string, unknown>
  }
): Promise<{ fieldValues: FieldValues; documentsUsed: string[] }> {
  const response = await axios.post(`${gongAgentUrl}/api/v1/assess/section`, {
    ticket_id: ticketId,
    section_id: sectionId,
    ...(inputs && { inputs }),
  })
  const stamped = stampArrayRowIds(
    FieldValuesSchema.parse(response.data?.field_values ?? {})
  )
  const documentsUsed: string[] = response.data?.documents_used ?? []
  const fieldValues = Object.fromEntries(
    Object.entries(stamped).map(([k, fv]) => [
      k,
      withFieldOrigin(
        fv.value === null || fv.value === undefined
          ? { ...fv, aiExtracted: false, documentsSearched: documentsUsed }
          : fv,
        'llm'
      ),
    ])
  )
  return { fieldValues, documentsUsed }
}

/**
 * Narrows an unknown thrown value to the axios-style error envelope we read
 * (HTTP status + server `detail` message) without depending on axios types.
 */
function getAxiosError(err: unknown): { status?: number; detail?: string } {
  const e = err as {
    response?: { status?: number; data?: { detail?: string } }
  }
  return { status: e?.response?.status, detail: e?.response?.data?.detail }
}

/** Runs an agent section assessment; callers must pass stable, memoized inputs. */
export function useGongAgentAutofill({
  sectionId,
  onFieldValues,
  inputs,
}: {
  sectionId: string
  onFieldValues: (
    fieldValues: FieldValues,
    meta: { documentsUsed: string[] }
  ) => void
  inputs?: Record<string, unknown>
}) {
  const { GONG_AGENT_URL } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()
  const { ticketId } = useTicketAccess()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [documentsUsed, setDocumentsUsed] = useState<string[]>([])
  const [extractedAny, setExtractedAny] = useState<boolean | null>(null)

  const blockedReason = usePrefillBlockedReason()

  const autofill = useCallback(async () => {
    if (blockedReason) return
    if (!GONG_AGENT_URL) {
      setError('GONG_AGENT_URL is not configured')
      return
    }
    setLoading(true)
    setError(null)
    setDocumentsUsed([])
    setExtractedAny(null)
    try {
      const { fieldValues: markedFieldValues, documentsUsed } =
        await postAssessSection(axios, GONG_AGENT_URL, {
          ticketId,
          sectionId,
          inputs,
        })
      onFieldValues(markedFieldValues, { documentsUsed })
      setDocumentsUsed(documentsUsed)
      setExtractedAny(
        Object.values(markedFieldValues).some(
          (fv) =>
            fv.aiExtracted !== false &&
            !(Array.isArray(fv.value) && fv.value.length === 0)
        )
      )
    } catch (err: unknown) {
      setError(getAxiosError(err).detail ?? 'AI pre-fill failed')
    } finally {
      setLoading(false)
    }
  }, [
    blockedReason,
    GONG_AGENT_URL,
    axios,
    ticketId,
    sectionId,
    onFieldValues,
    inputs,
  ])

  return {
    autofill,
    loading,
    error,
    documentsUsed,
    extractedAny,
  }
}

export const AiAutofillBanner: FC<{
  section: { id: string; agentPermissions?: string[] }
  onFieldValues: (fieldValues: FieldValues) => void
}> = ({ section, onFieldValues }) => {
  const { autofill, loading, error, documentsUsed, extractedAny } =
    useGongAgentAutofill({
      sectionId: section.id,
      onFieldValues,
    })
  const {
    allProcessed,
    isCheckingProcessing,
    unprocessedCount,
    failedEntryIds,
    refreshProcessingStatus,
  } = useDocumentProcessingReady()
  const reprocessDocuments = useReprocessDocuments()

  const [reprocessing, setReprocessing] = useState(false)
  const [reprocessError, setReprocessError] = useState<string | null>(null)

  const docsNotReady = !allProcessed && !isCheckingProcessing
  const hasFailures = failedEntryIds.length > 0

  const onReprocess = useCallback(async () => {
    setReprocessing(true)
    setReprocessError(null)
    try {
      await reprocessDocuments(failedEntryIds)
    } catch (err: unknown) {
      const { status, detail } = getAxiosError(err)
      setReprocessError(
        status === 403
          ? 'You need Rolodex admin access to reprocess documents.'
          : (detail ?? 'Failed to reprocess documents')
      )
      setReprocessing(false)
      return
    }
    // Re-query once without polling; the user refreshes to watch reprocessing.
    refreshProcessingStatus()
  }, [reprocessDocuments, failedEntryIds, refreshProcessingStatus])

  // Derive type and description together so the banner state cannot drift.
  const { alertType, description } = useMemo<{
    alertType: 'success' | 'info' | 'warning' | 'error'
    description?: string
  }>(() => {
    if (reprocessError)
      return { alertType: 'error', description: reprocessError }
    if (reprocessing)
      return {
        alertType: 'info',
        description:
          'Reprocessing started. Documents are being re-queued; refresh to check progress.',
      }
    if (error) return { alertType: 'error', description: error }
    if (documentsUsed.length > 0)
      return extractedAny
        ? { alertType: 'success', description: undefined }
        : {
            alertType: 'warning',
            description:
              'Documents were found but no data could be extracted. Fields need to be filled manually.',
          }
    if (extractedAny === false)
      return {
        alertType: 'warning',
        description:
          'No relevant documents were found. Please ensure Documents are uploaded and processed.',
      }
    if (hasFailures)
      return {
        alertType: 'warning',
        description: `${failedEntryIds.length} document${failedEntryIds.length === 1 ? '' : 's'} failed processing. Reprocess to try again.`,
      }
    if (docsNotReady)
      return {
        alertType: 'warning',
        description: `${unprocessedCount} document${unprocessedCount === 1 ? '' : 's'} still processing. Pre-fill with AI will be available once all documents are processed.`,
      }
    return { alertType: 'info', description: undefined }
  }, [
    reprocessError,
    reprocessing,
    error,
    documentsUsed,
    extractedAny,
    hasFailures,
    failedEntryIds,
    docsNotReady,
    unprocessedCount,
  ])

  const buttonDisabled = docsNotReady || isCheckingProcessing

  return (
    <AiPrefillGate
      permissions={
        (section.agentPermissions as RolodexPermissionsValues[]) ?? []
      }>
      <AiAutofillAlert
        icon={
          loading || reprocessing ? (
            <LoadingOutlined spin />
          ) : (
            <ThunderboltOutlined />
          )
        }
        showIcon
        type={alertType}
        message={prefillLabel({ loading: false, hasRun: false })}
        description={description}
        action={
          !loading && (
            <Space>
              {hasFailures && (
                <Button
                  size='small'
                  icon={<ReloadOutlined />}
                  onClick={onReprocess}
                  loading={reprocessing}
                  disabled={reprocessing}>
                  Reprocess failed
                </Button>
              )}
              <AiPrefillButton
                type='primary'
                size='small'
                icon={<ThunderboltOutlined />}
                onClick={autofill}
                disabled={buttonDisabled}
                tooltip={
                  buttonDisabled
                    ? 'Documents are still being processed'
                    : undefined
                }>
                {prefillLabel({
                  loading,
                  hasRun: extractedAny !== null,
                })}
              </AiPrefillButton>
            </Space>
          )
        }
      />
    </AiPrefillGate>
  )
}
